import React, { useState, useEffect } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { Flex, Grid, GridItem, Stack, Text, useColorModeValue } from "@chakra-ui/react";
import GenerateMissingQRCodes from "../GenerateMissingQRCodes";
import RegenerateAllQRCodes from "../RegenerateAllQRCodes";
import MigrationDialog from "./MigrationDialog";
import CacheActionsPanel from "./CacheActionsPanel";
import CacheStatsCard from "./CacheStatsCard";

/**
 * Fetches cache statistics from the admin API.
 * @param {Function} setStatsLoading - Setter for loading state
 * @param {Function} setCacheStats - Setter for cache stats data
 * @returns {Promise<void>}
 */
async function fetchCacheStats(setStatsLoading, setCacheStats) {
    setStatsLoading(true);
    await axios
        .get("/admin/tools/cache_stats")
        .then((response) => {
            setCacheStats(response.data.cache_stats);
            setStatsLoading(false);
        })
        .catch(() => {
            setStatsLoading(false);
        });
}

/**
 * Handles the migration result and shows an appropriate toast.
 * @param {object} response - Axios response with migration data
 * @returns {void}
 */
function showMigrationToast(response) {
    const migration = response.data.contact_cards_migration;
    if (migration?.migrated_count > 0) {
        toast.success(`${migration.migrated_count} contact card(s) migrated successfully`);
    } else if (migration?.skipped_count > 0) {
        toast(`${migration.skipped_count} contact card(s) skipped`);
    } else {
        toast.success("No cards to migrate");
    }
}

/**
 * Builds a fallback error result object for migration failures.
 * @param {Error} error - The caught error
 * @returns {object} Structured migration error result
 */
function buildMigrationErrorResult(error) {
    return {
        contact_cards_migration: {
            total_declined_found: 0,
            errors: [{ error: error.message }],
            errors_count: 1,
            migrated: [],
            migrated_count: 0,
            skipped: [],
            skipped_count: 0,
        },
    };
}

/**
 * Runs the migration API call and updates state with results.
 * @param {Function} setLoading - Loading state setter
 * @param {Function} setModal - Modal open state setter
 * @param {Function} setResult - Result state setter
 */
async function runMigration(setLoading, setModal, setResult) {
    setLoading(true);
    setModal(true);
    setResult(null);
    await axios
        .post("/admin/tools/fix_database_migration")
        .then((response) => {
            setResult(response.data);
            setLoading(false);
            showMigrationToast(response);
        })
        .catch((error) => {
            setResult(buildMigrationErrorResult(error));
            setLoading(false);
            toast.error("Error during migration");
        });
}

/**
 * Caches all contact cards via API and refreshes stats.
 * @param {Function} setLoading - Loading state setter
 * @param {Function} setStatsLoading - Stats loading setter
 * @param {Function} setCacheStats - Cache stats setter
 */
async function cacheCards(setLoading, setStatsLoading, setCacheStats) {
    setLoading(true);
    await axios
        .post("/admin/tools/cache_contact_cards")
        .then((response) => {
            toast.success(`${response.data.cache_operation?.cached_count || 0} card(s) cached`);
            setLoading(false);
            fetchCacheStats(setStatsLoading, setCacheStats);
        })
        .catch(() => {
            toast.error("Error caching contact cards");
            setLoading(false);
        });
}

/**
 * Clears contact cards cache via API and refreshes stats.
 * @param {Function} setLoading - Loading state setter
 * @param {Function} setStatsLoading - Stats loading setter
 * @param {Function} setCacheStats - Cache stats setter
 */
async function clearCache(setLoading, setStatsLoading, setCacheStats) {
    setLoading(true);
    await axios
        .post("/admin/tools/clear_contact_cards_cache")
        .then((response) => {
            toast.success(`${response.data.keys_deleted} key(s) removed from cache`);
            setLoading(false);
            fetchCacheStats(setStatsLoading, setCacheStats);
        })
        .catch(() => {
            toast.error("Error clearing cache");
            setLoading(false);
        });
}

/**
 * Closes the migration dialog and resets the migration result.
 * @param {Function} setOpenMigrationModal - Modal open state setter
 * @param {Function} setMigrationResult - Migration result state setter
 */
function closeMigrationModal(setOpenMigrationModal, setMigrationResult) {
    setOpenMigrationModal(false);
    setMigrationResult(null);
}

/**
 * Renders the cache actions panel and stats card in a responsive grid layout.
 * @param {Object} props - All cache action and stats props
 * @returns {React.ReactElement}
 */
function DatabaseUpdateGrid(props) {
    const { migrationLoading, cacheLoading, clearCacheLoading, statsLoading,
        cacheStats, onMigrate, onCacheCards, onClearCache, onRefreshStats } = props;
    return (
        <Grid templateColumns={{ base: "1fr", md: "1fr 2fr" }} gap="20px">
            <GridItem>
                <CacheActionsPanel migrationLoading={migrationLoading} cacheLoading={cacheLoading}
                    clearCacheLoading={clearCacheLoading} statsLoading={statsLoading}
                    onMigrate={onMigrate} onCacheCards={onCacheCards}
                    onClearCache={onClearCache} onRefreshStats={onRefreshStats} />
            </GridItem>
            <GridItem>
                <Stack spacing="20px">
                    <CacheStatsCard loading={statsLoading} cacheStats={cacheStats} />
                </Stack>
            </GridItem>
        </Grid>
    );
}

/**
 * Main database management page. Orchestrates migration, cache operations,
 * and cache statistics display via sub-components.
 * @returns {React.ReactElement}
 */
function DatabaseUpdate() {
    const [openMigrationModal, setOpenMigrationModal] = useState(false);
    const [migrationLoading, setMigrationLoading] = useState(false);
    const [migrationResult, setMigrationResult] = useState(null);
    const [cacheLoading, setCacheLoading] = useState(false);
    const [clearCacheLoading, setClearCacheLoading] = useState(false);
    const [cacheStats, setCacheStats] = useState(null);
    const [statsLoading, setStatsLoading] = useState(false);
    const titleColor = useColorModeValue("secondaryGray.900", "white");

    useEffect(() => { fetchCacheStats(setStatsLoading, setCacheStats); }, []);

    return (
        <Flex direction="column" gap="20px">
            <Text fontSize="2xl" fontWeight="700" color={titleColor}>Database management</Text>
            <GenerateMissingQRCodes />
            <RegenerateAllQRCodes />
            <DatabaseUpdateGrid migrationLoading={migrationLoading} cacheLoading={cacheLoading}
                clearCacheLoading={clearCacheLoading} statsLoading={statsLoading} cacheStats={cacheStats}
                onMigrate={() => runMigration(setMigrationLoading, setOpenMigrationModal, setMigrationResult)}
                onCacheCards={() => cacheCards(setCacheLoading, setStatsLoading, setCacheStats)}
                onClearCache={() => clearCache(setClearCacheLoading, setStatsLoading, setCacheStats)}
                onRefreshStats={() => fetchCacheStats(setStatsLoading, setCacheStats)} />
            <MigrationDialog open={openMigrationModal} loading={migrationLoading} result={migrationResult}
                onClose={() => closeMigrationModal(setOpenMigrationModal, setMigrationResult)} />
        </Flex>
    );
}

export default DatabaseUpdate;
